import React from 'react'

interface State {
  error: Error | null
  info: string
}

/**
 * Error boundary that catches React render errors and displays
 * the full error message instead of crashing the page.
 */
export default class RenderBoundary extends React.Component<
  { children: React.ReactNode; label?: string },
  State
> {
  state: State = { error: null, info: '' }

  static getDerivedStateFromError(error: Error) {
    return { error }
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    const info = errorInfo.componentStack || ''
    this.setState({ info })
    console.error(`[RenderBoundary] ${error.message}`, { error, componentStack: info })
  }

  render() {
    if (this.state.error) {
      return (
        <div
          style={{
            padding: 12,
            margin: '8px 0',
            borderRadius: 8,
            background: 'rgba(248,113,113,0.08)',
            border: '1px solid rgba(248,113,113,0.3)',
            fontSize: 12,
            fontFamily: 'monospace',
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
          }}
        >
          <div style={{ fontWeight: 700, color: '#f87171', marginBottom: 6 }}>
            Render error{this.props.label ? ` in ${this.props.label}` : ''}
          </div>
          <div>{this.state.error.message}</div>
          {this.state.info && (
            <details style={{ marginTop: 8 }}>
              <summary style={{ cursor: 'pointer', color: '#999' }}>Component stack</summary>
              <div style={{ marginTop: 4, color: '#999' }}>{this.state.info}</div>
            </details>
          )}
        </div>
      )
    }
    return this.props.children
  }
}
